import React from "react";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { COLORS } from "./constants";
import { FennMark } from "../FennLoader";

// ─────────────────────────────────────────────────────────────────────────────
// Mp4Dropzone
//
// The upload target for MP4 scheduling. It used to sit at the bottom of the MP4
// panel, below the source picker; it now fills the preview frame at the top,
// which is where the eye already is when "MP4 file" is the chosen source — and
// where the file is about to appear once it uploads.
//
// It also finally does what its own label promised. The old version said "or
// drag an MP4 here" but wired no drag handlers at all: dropping a file on it
// did nothing, and because a browser's default action for a dropped file is to
// NAVIGATE to it, the dashboard would be replaced by the raw video. That is a
// worse outcome than nothing happening.
//
// Three details that matter for drag and drop working at all:
//
//   • onDragOver must call preventDefault(), or the drop event never fires.
//   • dragleave fires when the pointer crosses onto a CHILD element, so a naive
//     handler makes the highlight flicker. A depth counter tracks enter/leave
//     pairs instead.
//   • The drop is validated here rather than trusted: a drag can carry
//     directories, several files at once, or a .mkv someone renamed.
// ─────────────────────────────────────────────────────────────────────────────

export function Mp4Dropzone({
  fileInputRef, uploadState, handleFileSelect, abortUpload, onNotice
}) {
  const [dragging, setDragging] = React.useState(false);
  const depth = React.useRef(0);

  const uploading = uploadState.uploading;
  const errored   = Boolean(uploadState.error);
  const done      = !uploading && !errored && uploadState.progress >= 100;

  const accept = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp4")) {
      onNotice?.("Only MP4 files are allowed", "warning");
      return;
    }
    handleFileSelect(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (uploading) return;

    const items = event.dataTransfer?.files;
    if (!items || items.length === 0) return;
    if (items.length > 1) {
      onNotice?.("Drop one file at a time", "warning");
      return;
    }
    accept(items[0]);
  };

  const onDragEnter = (event) => {
    event.preventDefault();
    if (uploading) return;
    depth.current += 1;
    setDragging(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  };

  const borderColor = errored  ? COLORS.red
                    : dragging ? COLORS.cyan
                    : uploading ? COLORS.cyan
                    : "rgba(255,255,255,0.14)";

  return (
    <Box
      onClick={() => !uploading && fileInputRef.current?.click()}
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      // preventDefault here is what makes the element a valid drop target.
      onDragOver={(e) => e.preventDefault()}
      role="button"
      tabIndex={uploading ? -1 : 0}
      aria-label="Upload an MP4 video"
      onKeyDown={(e) => {
        if (uploading) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); }
      }}
      sx={{
        width: "100%", height: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 1, px: 3, textAlign: "center",
        cursor: uploading ? "default" : "pointer",
        border: `1.5px dashed ${borderColor}`,
        borderRadius: "12px",
        bgcolor: dragging ? "rgba(43,189,175,0.07)" : "transparent",
        transition: "border-color .15s, background-color .15s",
        outline: "none",
        "&:focus-visible": { borderColor: COLORS.cyan, bgcolor: "rgba(43,189,175,0.05)" },
        "&:hover": uploading ? {} : { borderColor: COLORS.cyan, bgcolor: "rgba(43,189,175,0.04)" }
      }}
    >
      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept="video/mp4,.mp4"
        onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ""; }}
      />

      {errored ? (
        <ErrorOutlineIcon sx={{ fontSize: 36, color: COLORS.red }} />
      ) : uploading ? (
        <FennMark size={40} />
      ) : done ? (
        <CloudDoneIcon sx={{ fontSize: 36, color: COLORS.cyan }} />
      ) : (
        <CloudUploadIcon sx={{ fontSize: 36, color: dragging ? COLORS.cyan : "#2A3340" }} />
      )}

      {uploading ? (
        <>
          <Typography noWrap sx={{ color: COLORS.text, fontWeight: 600, fontSize: 13, maxWidth: "90%" }}>
            {uploadState.fileName}
          </Typography>
          <Box sx={{ width: "min(340px, 80%)" }}>
            <LinearProgress
              variant="determinate"
              value={uploadState.progress}
              sx={{
                height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)",
                "& .MuiLinearProgress-bar": { bgcolor: COLORS.cyan, borderRadius: 3 }
              }}
            />
          </Box>
          <Typography sx={{ color: COLORS.muted, fontSize: 11.5, fontFamily: "monospace" }}>
            {uploadState.progress}% · {uploadState.chunksDone}/{uploadState.chunksTotal} chunks
          </Typography>
          <Button
            size="small"
            // Without this the click bubbles to the parent and reopens the file
            // picker on top of the upload the user is trying to cancel.
            onClick={(e) => { e.stopPropagation(); abortUpload(uploadState.uploadId); }}
            sx={{
              mt: 0.5, textTransform: "none", fontSize: 11.5, fontWeight: 600,
              color: COLORS.red, "&:hover": { bgcolor: "rgba(229,72,77,0.08)" }
            }}
          >
            Cancel upload
          </Button>
        </>
      ) : errored ? (
        <>
          <Typography sx={{ color: COLORS.red, fontWeight: 600, fontSize: 13 }}>
            Upload failed
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: 11.5, maxWidth: 340, lineHeight: 1.5 }}>
            {uploadState.error}
          </Typography>
          <Typography sx={{ color: COLORS.cyan, fontSize: 11.5, fontWeight: 700, mt: 0.5 }}>
            Click to try another file
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ color: COLORS.text, fontWeight: 600, fontSize: 14 }}>
            <Box component="span" sx={{ color: COLORS.cyan }}>Browse</Box> or drag an MP4 here
          </Typography>
          <Typography sx={{ color: "#5A6472", fontSize: 11.5, lineHeight: 1.5, maxWidth: 340 }}>
            Uploaded once in 50 MB chunks, up to 8 GB. Schedule it below to stream at a set time.
          </Typography>
        </>
      )}
    </Box>
  );
}
